import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { BarChart3, TrendingUp, Layers, Target, Sparkles } from "lucide-react";

const SEL = "bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-paper";

function Bar({ label, count, max, accent = "#5B3DF5" }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-40 text-paper/60 truncate">{label}</div>
      <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden"><div style={{ width: `${pct}%`, background: accent }} className="h-full" /></div>
      <div className="w-8 text-right text-paper/70 tabular-nums">{count}</div>
    </div>
  );
}

function Panel({ icon: Icon, title, children }) {
  return (
    <div className="border border-white/10 rounded-md p-5 bg-white/[0.03]">
      <div className="flex items-center gap-2 text-heraldic mb-4"><Icon className="h-4 w-4" /><span className="text-sm font-medium text-paper/80">{title}</span></div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function SITFCohort() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ cohort: "", period: "", programme: "" });
  const load = () => {
    const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/admin/sitf/cohort${qs ? `?${qs}` : ""}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const maxLevel = data ? Math.max(1, ...data.level_distribution.map((d) => d.count)) : 1;
  const maxTraj = data ? Math.max(1, ...data.trajectory_distribution.map((d) => d.count)) : 1;

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl" data-testid="sitf-cohort-page">
        <div className="eyebrow text-heraldic mb-1">Academic analytics</div>
        <h1 className="font-serif text-3xl">Cohort Trajectory Dashboard</h1>
        <p className="text-paper/50 text-sm mt-1">Aggregated, anonymised SITF signals across approved assessments — for curriculum and cohort-level decisions. No individual family report is exposed here.</p>

        {data && (
          <div className="mt-5 flex flex-wrap gap-2 items-center" data-testid="cohort-filters">
            <select value={filters.cohort} onChange={(e) => setFilters({ ...filters, cohort: e.target.value })} className={SEL}><option value="">All cohorts</option>{data.filters.cohorts.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })} className={SEL}><option value="">All periods</option>{data.filters.periods.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <span className="text-paper/50 text-sm ml-2" data-testid="cohort-count">{data.count} assessment{data.count === 1 ? "" : "s"}</span>
          </div>
        )}

        {data && data.count === 0 && <div className="mt-8 text-paper/40 text-sm">No approved assessments match these filters yet.</div>}

        {data && data.count > 0 && (
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <Panel icon={BarChart3} title="Level distribution (N0–N5)">
              {data.level_distribution.map((d) => <Bar key={d.level} label={`${d.level} · ${d.label}`} count={d.count} max={maxLevel} />)}
            </Panel>
            <Panel icon={TrendingUp} title="Trajectory distribution">
              {data.trajectory_distribution.map((d) => <Bar key={d.key} label={d.label} count={d.count} max={maxTraj} accent="#10b981" />)}
            </Panel>
            <Panel icon={Target} title="Top priority development zones">
              {data.top_priorities.length === 0 ? <div className="text-paper/40 text-sm">—</div> : data.top_priorities.map((d, i) => <Bar key={i} label={d.label} count={d.count} max={Math.max(1, ...data.top_priorities.map((x) => x.count))} accent="#f59e0b" />)}
            </Panel>
            <Panel icon={Sparkles} title="Most observed strengths">
              {data.top_strengths.length === 0 ? <div className="text-paper/40 text-sm">—</div> : data.top_strengths.map((d, i) => <Bar key={i} label={d.label} count={d.count} max={Math.max(1, ...data.top_strengths.map((x) => x.count))} accent="#06b6d4" />)}
            </Panel>
            <div className="md:col-span-2">
              <Panel icon={Layers} title="Depth of mastery by domain">
                <div className="space-y-4">
                  {data.domain_mastery.map((dm) => {
                    const m = Math.max(1, ...dm.levels.map((x) => x.count));
                    return (
                      <div key={dm.domain}>
                        <div className="text-paper/70 text-sm mb-1">{dm.domain}</div>
                        {dm.levels.map((l) => <Bar key={l.key} label={l.label} count={l.count} max={m} accent="#8b5cf6" />)}
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
